import { gql } from '@apollo/client';
import {
  Box,
  Button,
  Image,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import NextLink from 'next/link';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { useCommentActions } from '@/components/Post/hooks/useCommentActions';
import {
  PostingExperience,
  usePostingExperience,
} from '@/components/PostingExperience';
import { usePostHref } from '@/hooks/usePostHref';
import { Card } from '@/imports/ui/chakra/components/Card';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';

import { Blog_BlogV2Fragment } from './graphql-types/Blog_BlogV2Fragment';

import { Comments } from '../Comments';
import { CreateComment, CreateComment_postFragment } from '../CreateComment';
import { LikeCommentFooter } from '../LikeCommentFooter';
import { PostHeader } from '../PostHeader';
import { Tags } from '../Tags/Tags';

type PostHeaderProps = React.ComponentProps<typeof PostHeader>;

export const Blog: React.FC<{
  blog: Blog_BlogV2Fragment;
  options?: PostHeaderProps['options'] & { isExpandedByDefault?: boolean };
}> & {
  blogFragment: DocumentNode;
} = ({ blog, options }) => {
  const postHref = usePostHref(blog._id);

  const {
    ref,
    focusAndScrollToRef,
    expandOrFocusAndScrollToRef,
    isActive,
    setIsActive,
    ...expandProps
  } = useCommentActions({
    post: blog,
    isExpandedByDefault: options?.isExpandedByDefault,
  });
  const postingExperience = usePostingExperience();

  return (
    <Box p="4">
      <PostHeader post={blog} mb="4" options={options} />
      <Card>
        <LinkBox>
          {blog.bannerImage?.imageProxy && (
            <Image
              borderTopLeftRadius="xl"
              borderTopRightRadius="xl"
              w="full"
              src={blog.bannerImage?.imageProxy || undefined}
              fallback={<ImageFallback aspectRatio="8/3" />}
              alt=""
            />
          )}
          <Stack p="4">
            <LinkOverlay as={NextLink} href={postHref}>
              {blog.blogTitle && (
                <Text
                  fontSize="30px"
                  lineHeight="36px"
                  fontWeight="600"
                  color="gray.700"
                >
                  {blog.blogTitle}
                </Text>
              )}
            </LinkOverlay>

            {blog.content && (
              <AbbreviateRedraft
                options={{ defaultIsOpen: options?.isExpandedByDefault }}
                rawDraftContentState={blog.content}
                noOfCharacters={500}
              />
            )}

            {blog.published && (
              <NextLink href={postHref}>
                <Button w="full" variant="outline" colorScheme="gray">
                  Read Full Article
                </Button>
              </NextLink>
            )}
          </Stack>
        </LinkBox>
      </Card>
      <VStack spacing="4" width="full" align="start" mt="4">
        <Tags post={blog} />
        <LikeCommentFooter
          post={blog}
          onClickComment={focusAndScrollToRef}
          onClickCommentCount={expandOrFocusAndScrollToRef}
        />
        <Comments post={blog} expandProps={expandProps} />
        <CreateComment
          post={blog}
          ref={ref}
          isActive={isActive}
          setIsActive={setIsActive}
        />
      </VStack>
      <PostingExperience
        {...postingExperience}
        onDrawerClose={() => {
          postingExperience.onDrawerClose();
        }}
        options={{
          postId: blog._id,
          tags: blog.tags ?? undefined,
          group: blog.group ? blog.group : undefined,
          organization: blog.organization ? blog.organization : undefined,
        }}
      />
    </Box>
  );
};

Blog.blogFragment = gql`
  fragment Blog_BlogV2Fragment on BlogV2 {
    _id
    blogTitle: title
    content
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
    published
    currentUserCanUpdatePost
    ...PostHeader_PostFragment
    ...Tags_PostFragment
    ...LikeCommentFooter_PostFragment
    ...Comments_PostFragment
    ...CreateComment_PostFragment
    group {
      _id
      name
    }
    organization {
      _id
      name
    }
    tags {
      _id
      name
    }
    ...useCommentActions_PostFragment
  }
  ${PostHeader.postFragment}
  ${Tags.postFragment}
  ${LikeCommentFooter.postFragment}
  ${Comments.postFragment}
  ${CreateComment_postFragment}
  ${useCommentActions.postFragment}
`;
